import { SerpAppearanceCard } from '../components/SerpAppearanceCard';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { ApiTokensCard } from '../components/ApiTokensCard';
import { EditorOnly } from '../components/EditorOnly';
import { PageHeader } from '../components/PageHeader';
import { WorkspaceManagementDialog } from '../components/WorkspaceManagementDialog';
import { Box, Stack, Typography } from '../components/mui-system';
import { useWorkspace } from '../workspace-context';

export function SettingsPage() {
  const { activeWorkspace, user, createWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace!.id;
  const [personalEnabled, setPersonalEnabled] = useState(false);
  const [workspaceManagementOpen, setWorkspaceManagementOpen] = useState(false);
  const [workspaceForm, setWorkspaceForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    api
      .workspacePolicy()
      .then((policy) => {
        if (active) setPersonalEnabled(policy.personalWorkspacesEnabled);
      })
      .catch((caught) => {
        if (active) setError(message(caught, 'Не удалось загрузить настройки'));
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <Box>
      <PageHeader
        title="Настройки"
        description="Параметры рабочего пространства и ваши персональные API-токены."
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {workspaceManagementOpen && (
        <WorkspaceManagementDialog onClose={() => setWorkspaceManagementOpen(false)} />
      )}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography fontWeight={800}>
                {activeWorkspace!.name} ·{' '}
                {activeWorkspace!.isShared ? 'Общий воркспейс' : 'Личный воркспейс'}
              </Typography>
              <Typography color="text.secondary" fontSize={13}>
                Общий воркспейс доступен всем пользователям, которым разрешён вход в дашборд.
              </Typography>
            </Box>
            <Stack direction="row" gap={1}>
              {personalEnabled && (
                <EditorOnly permissions="settings.create">
                  <Button onClick={() => setWorkspaceForm(true)}>Создать личный воркспейс</Button>
                </EditorOnly>
              )}
              {user.isAdmin && (
                <Button onClick={() => setWorkspaceManagementOpen(true)}>
                  Управление воркспейсами
                </Button>
              )}
              {user.isAdmin && (
                <Button href={`/admin/audit?workspaceId=${encodeURIComponent(workspaceId)}`}>
                  Журнал действий
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <SerpAppearanceCard key={workspaceId} />
      <ApiTokensCard key={user.id} />
      <Dialog open={workspaceForm} onClose={() => setWorkspaceForm(false)} fullWidth maxWidth="sm">
        <Box
          component="form"
          onSubmit={async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            setSaving(true);
            try {
              await createWorkspace({
                name: String(data.get('name')),
                siteUrl: String(data.get('siteUrl')),
              });
              setWorkspaceForm(false);
            } catch (caught) {
              setError(message(caught, 'Не удалось создать воркспейс'));
            } finally {
              setSaving(false);
            }
          }}
        >
          <DialogTitle>Личный воркспейс</DialogTitle>
          <DialogContent>
            <Stack gap={2} pt={1}>
              <TextField name="name" label="Название" required />
              <TextField name="siteUrl" label="URL сайта" type="url" required />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkspaceForm(false)}>Отмена</Button>
            <Button type="submit" disabled={saving}>
              Создать
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}

function message(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}
